// client/src/components/PortfolioStockHeader.js
import React, { useState, useEffect, useRef } from 'react';
import SortingControl from './SortingControl';
import config from '../config/config';

const PortfolioStockHeader = ({
    sortBy, 
    onSortChange, 
    sortOrder, 
    onSortOrderChange,
    portfolio = [], 
    onFilterChange,
    onAddStock,
    preferredCurrency,
    initialStockId = null,
    onInitialStockHandled
}) => {
    // Stock search and selection state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [showQuantityModal, setShowQuantityModal] = useState(false);
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);
    
    // Purchase date state
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const [purchaseDate, setPurchaseDate] = useState(oneYearAgo.toISOString().split('T')[0]);
    
    // Filter state
    const [activeFilter, setActiveFilter] = useState(null);
    
    // Create a ref to store the original stock ID
    const originalStockIdRef = useRef(null);
    
    // Store search request cancel function
    const abortControllerRef = useRef(null);

    // Search function with debounce and request cancellation
    const handleSearch = async (value) => {
        // Update search term immediately
        setSearchTerm(value);
        
        // Clear previous results if search is too short
        if (value.length < 2) {
            setSearchResults([]);
            return;
        }
        
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new abort controller
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        
        setIsSearching(true);
        try {
            const response = await fetch(config.apiUrl + '/api/stocks/search-by-name', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: value }),
                signal: abortController.signal
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            // Only update if this is still the latest request
            if (!abortController.signal.aborted) {
                setSearchResults(data.data || []);
            }
        } catch (error) {
            // Ignore aborted requests
            if (error.name !== 'AbortError') {
                console.error('Search failed:', error);
                setSearchResults([]);
            }
        } finally {
            if (!abortController.signal.aborted) {
                setIsSearching(false);
            }
        }
    };

    // Stock selection handler with improved state tracking
    const handleStockSelect = (basicStockInfo) => {
        
        // Store the original ID in the ref
        originalStockIdRef.current = basicStockInfo.id;
        
        // Set initial stock info with basic data
        setSelectedStock({
            ...basicStockInfo,
            originalId: basicStockInfo.id,  // Also store in the state object
            quantity: 1
        });
        
        setShowQuantityModal(true);
        setSearchResults([]);
        setSearchTerm('');
        
        // Reset purchase date to default
        setPurchaseDate(oneYearAgo.toISOString().split('T')[0]);
        
        // Fetch complete data in the background
        setIsFetchingDetails(true);
        fetchCompleteStockData(basicStockInfo.id)
            .then(completeData => {
                if (completeData) {
                    setSelectedStock(prevStock => ({
                        ...completeData,
                        originalId: basicStockInfo.id,  // Preserve the original ID
                        quantity: prevStock?.quantity || 1
                    }));
                }
            })
            .catch(error => {
                console.error('Error fetching stock details:', error);
                // Keep the basic stock info if detailed fetch fails
            })
            .finally(() => {
                setIsFetchingDetails(false);
            });
    };

    // Portfolio addition handler with improved ID handling
    const handleAddToPortfolio = () => {
        if (!selectedStock || quantity <= 0) return;
        
        // Use the original ID we stored when selecting the stock
        const stockId = originalStockIdRef.current || selectedStock.originalId || selectedStock.id;
        
        onAddStock({
            id: stockId,
            name: selectedStock.name,
            quantity: Number(quantity),
            purchaseDate: purchaseDate  // No need to send price, backend calculates it
        });
        
        // Reset state
        setShowQuantityModal(false);
        setSelectedStock(null);
        setQuantity(1);
        setPurchaseDate(oneYearAgo.toISOString().split('T')[0]);
        originalStockIdRef.current = null;
    };

    // Fetch detailed stock data with error handling
    const fetchCompleteStockData = async (stockId) => {
        try {
            // Use the stock ID to fetch the specific stock
            const response = await fetch(`${config.apiUrl}/api/stocks/${stockId}?currency=${preferredCurrency}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            if (data.success && data.data) {
                return data.data;
            }
            throw new Error('Failed to fetch stock details');
        } catch (error) {
            console.error(`Failed to fetch stock details for ID ${stockId}:`, error);
            return null;
        }
    };

    // Predefined filters for value investors
    const valueFilters = [
        {
            id: 'all',
            label: 'Alle',
            icon: 'fa-th-list',
            description: 'Zeigt alle Aktien',
            filterFn: (stocks) => stocks
        },
        {
            id: 'best_month',
            label: 'Top 30T',
            icon: 'fa-chart-line',
            description: 'Zeigt die Top-Performer des letzten Monats',
            filterFn: (stocks) => {
                return [...stocks].sort((a, b) => (b.monthly_change ?? -Infinity) - (a.monthly_change ?? -Infinity)).slice(0, 10);
            }
        },
        {
            id: 'best_week',
            label: 'Top 7T',
            icon: 'fa-arrow-trend-up',
            description: 'Zeigt die Top-Performer der letzten Woche',
            filterFn: (stocks) => {
                return [...stocks].sort((a, b) => (b.weekly_change ?? -Infinity) - (a.weekly_change ?? -Infinity)).slice(0, 10);
            }
        },
        {
            id: 'undervalued',
            label: 'Niedriges KGV',
            icon: 'fa-tags',
            description: 'Aktien mit niedrigem KGV (unter 15)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.trailing_p_e && stock.trailing_p_e < 15);
            }
        },
        {
            id: 'book_value',
            label: 'Niedriges KBV',
            icon: 'fa-book',
            description: 'Aktien mit niedrigem KBV (unter 1.5)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.price_to_book && stock.price_to_book < 1.5);
            }
        },
        {
            id: 'high_dividend',
            label: 'Dividende',
            icon: 'fa-coins',
            description: 'Aktien mit hoher Dividendenrendite (über 3%)',
            filterFn: (stocks) => {
                return stocks.filter(stock => stock.dividend_yield && stock.dividend_yield > 3);
            }
        },
        {
            id: 'quality_growth',
            label: 'Wachstum',
            icon: 'fa-seedling',
            description: 'Hohe Gewinnmarge und gutes Wachstum',
            filterFn: (stocks) => {
                return stocks.filter(stock => 
                    stock.profit_margin > 0.15 && 
                    stock.growth_estimates?.next_year > 0.10
                );
            }
        }
    ];

    
    // Function to apply a filter
    const applyFilter = (filter) => {
        // Determine appropriate sort criteria based on filter
        const getSortCriteriaForFilter = (filterId) => {
            switch(filterId) {
                case 'best_month':
                    return { sortBy: 'monthly_change', sortOrder: 'desc' };
                case 'best_week':
                    return { sortBy: 'weekly_change', sortOrder: 'desc' };
                case 'undervalued':
                    return { sortBy: 'trailing_p_e', sortOrder: 'asc' };
                case 'book_value':
                    return { sortBy: 'price_to_book', sortOrder: 'asc' };
                case 'high_dividend':
                    return { sortBy: 'dividend_yield', sortOrder: 'desc' };
                case 'quality_growth':
                    return { sortBy: 'profit_margin', sortOrder: 'desc' };
                default:
                    return { sortBy: 'total_growth', sortOrder: 'desc' };
            }
        };
    
        if (activeFilter?.id === filter.id && filter.id !== 'all') {
            // If clicking the active filter, deactivate it
            setActiveFilter(valueFilters[0]); // Set to "All" filter
            onFilterChange(null);
            
            // Reset to default sorting
            onSortChange('total_growth');
            onSortOrderChange('desc');
        } else {
            // Apply the new filter
            setActiveFilter(filter);
            const filteredStocks = filter.filterFn(portfolio);
            onFilterChange(filteredStocks);
            
            // Update sorting based on filter
            const { sortBy, sortOrder } = getSortCriteriaForFilter(filter.id);
            onSortChange(sortBy);
            onSortOrderChange(sortOrder);
        }
    };

    // Deep link (/portfolio?addStock=ID): open the add dialog for that stock once
    const handledInitialRef = useRef(null);
    useEffect(() => {
        if (!initialStockId || handledInitialRef.current === initialStockId) return;
        handledInitialRef.current = initialStockId;
        (async () => {
            const data = await fetchCompleteStockData(initialStockId);
            if (data) {
                handleStockSelect({ id: data.id, name: data.name, sector: data.sector });
            }
            if (onInitialStockHandled) onInitialStockHandled();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialStockId]);

    // If no filter is active, set "All" as default
    useEffect(() => {
        if (!activeFilter && valueFilters.length > 0) {
            setActiveFilter(valueFilters[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter]);

    // Clean up abort controller on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <div className="portfolio-stock-header">
            <div className="header-top">
                <h2 className="section-title">Meine Aktien</h2>
                
                <div className="portfolio-search">
                    <div className="search-input-container">
                        <input
                            type="search"
                            className="modern-input"
                            placeholder="Aktie hinzufügen..."
                            aria-label="Aktie suchen und zum Portfolio hinzufügen"
                            autoComplete="off"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {isSearching && <div className="search-spinner" />}
                    </div>
                    {searchResults.length > 0 && (
                        <ul className="search-results-dropdown" role="listbox" aria-label="Suchergebnisse">
                            {searchResults.map(stock => (
                                <li
                                    key={stock.id}
                                    className="search-result-item"
                                    role="option"
                                    aria-selected={false}
                                    tabIndex={0}
                                    onClick={() => handleStockSelect(stock)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStockSelect(stock); } }}
                                >
                                    <div className="stock-name">{stock.name}</div>
                                    {stock.sector && (
                                        <div className="stock-sector">{stock.sector}</div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="filter-scroll-container">
                <div className="filter-cards">
                    {valueFilters.map(filter => (
                        <button 
                            type="button"
                            key={filter.id} 
                            className={`filter-card ${activeFilter?.id === filter.id ? 'active' : ''}`}
                            onClick={() => applyFilter(filter)}
                            title={filter.description}
                            aria-pressed={activeFilter?.id === filter.id}
                        >
                            <div className="filter-card-icon">
                                <i className={`fas ${filter.icon}`} aria-hidden="true"></i>
                            </div>
                            <div className="filter-card-content">
                                <span className="filter-card-label">{filter.label}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="sorting-controls">
                <SortingControl
                    sortBy={sortBy}
                    onSortChange={onSortChange}
                    sortOrder={sortOrder}
                    onSortOrderChange={onSortOrderChange}
                />
            </div>

            {/* Stock Addition Modal */}
            {showQuantityModal && selectedStock && (
                <div
                    className="modal-overlay"
                    onClick={() => {
                        setShowQuantityModal(false);
                        setSelectedStock(null);
                        originalStockIdRef.current = null;
                    }}
                >
                    <div
                        className="modern-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`${selectedStock.name} zum Portfolio hinzufügen`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-grabber" aria-hidden="true"></div>
                        <h3>{selectedStock.name}</h3>
                        <p className="sector-info">{selectedStock.sector}</p>
                        
                        {isFetchingDetails && (
                            <div className="fetching-indicator">
                                <div className="spinner"></div>
                                <p>Lade Aktiendetails...</p>
                            </div>
                        )}
                        
                        <div className="quantity-input">
                            <div className="label-with-info">
                                <label>Aktuelle Anzahl:</label>
                                <div className="tooltip">
                                    <i className="fas fa-question-circle info-icon"></i>
                                    <span className="tooltip-text">Die Anzahl der aktuell im Depot vorhanden Aktien.</span>
                                </div>
                            </div>
                            <input
                                type="number"
                                min="1"
                                inputMode="numeric"
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                className="modern-input"
                                placeholder="z.B. 100"
                                aria-label="Anzahl"
                            />
                        </div>

                        <div className="quantity-input">
                            <div className="label-with-info">
                                <label>Kaufdatum:</label>
                                <div className="tooltip">
                                    <i className="fas fa-question-circle info-icon"></i>
                                    <span className="tooltip-text">Die meisten Analysen beziehen sich auf das letzte Jahr. Für ältere Positionen reicht 'Vor mehr als einem Jahr'.</span>
                                </div>
                            </div>
                            <div className="date-input-container">
                                {purchaseDate === oneYearAgo.toISOString().split('T')[0] ? (
                                    <>
                                        <div 
                                            className="date-input-wrapper"
                                            onClick={() => {
                                                setPurchaseDate(new Date().toISOString().split('T')[0]);
                                            }}
                                        >
                                            <input
                                                type="text"
                                                value="Vor mehr als einem Jahr"
                                                className="modern-input date-text"
                                                readOnly
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <button 
                                                className="date-toggle-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPurchaseDate(new Date().toISOString().split('T')[0]);
                                                }}
                                                title="Genaues Datum auswählen"
                                            >
                                                <i className="fas fa-calendar-alt"></i>
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="date"
                                            value={purchaseDate}
                                            onChange={(e) => setPurchaseDate(e.target.value)}
                                            className="modern-input"
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        <button 
                                            className="date-toggle-btn"
                                            onClick={() => setPurchaseDate(oneYearAgo.toISOString().split('T')[0])}
                                            title="Vor mehr als einem Jahr"
                                        >
                                            <i className="fas fa-history"></i>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button 
                                type="button"
                                onClick={handleAddToPortfolio}
                                className="action-button primary"
                                disabled={isFetchingDetails}
                            >
                                <i className="fas fa-plus" aria-hidden="true"></i> Zum Portfolio hinzufügen
                            </button>
                            <button 
                                type="button"
                                onClick={() => {
                                    setShowQuantityModal(false);
                                    setSelectedStock(null);
                                    originalStockIdRef.current = null;
                                }}
                                className="action-button secondary"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioStockHeader;